import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { ConfirmationService } from 'src/app/shared/services/utilities/confirmation.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { mapTemplateError } from '../template-error';
import { TemplateFormDialogComponent, TemplateFormDialogData, TemplateFormMode } from '../template-form-dialog/template-form-dialog.component';
import { WorkClassificationTemplate, WorkClassificationTemplateDetail } from '../work-classification-template.models';
import { WorkClassificationTemplateService } from '../work-classification-template.service';

const T = 'WORKCLASSIFICATION.TEMPLATES.';

/** Template library: the company's shared classification trees. Route is CanManageWorkClassification only. */
@Component({
  selector: 'app-template-library',
  templateUrl: './template-library.component.html',
  styleUrls: ['./template-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe, DatePipe],
})
export class TemplateLibraryComponent implements OnInit {
  private service = inject(WorkClassificationTemplateService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private confirm = inject(ConfirmationService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  readonly columns = ['name', 'description', 'nodeCount', 'updatedAt', 'actions'];

  templates = signal<WorkClassificationTemplate[]>([]);
  loading = signal(true);
  loadFailed = signal(false);
  showInactive = signal(false);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.loadFailed.set(false);
    this.service
      .list(this.showInactive())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.templates.set(list);
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          this.loadFailed.set(true);
          this.logger.errorWithContext('TemplateLibraryComponent', 'Failed to load templates', err);
        },
      });
  }

  toggleInactive(checked: boolean): void {
    this.showInactive.set(checked);
    this.load();
  }

  open(template: WorkClassificationTemplate): void {
    this.router.navigate(['/worktime', 'classification', 'templates', template.id]);
  }

  openForm(mode: TemplateFormMode, template?: WorkClassificationTemplate): void {
    const data: TemplateFormDialogData = { mode, template };
    this.dialog
      .open<TemplateFormDialogComponent, TemplateFormDialogData, WorkClassificationTemplateDetail>(TemplateFormDialogComponent, {
        width: '36rem',
        maxWidth: '95vw',
        autoFocus: 'first-tabbable',
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(detail => {
        if (!detail) {
          return;
        }
        if (mode === 'edit') {
          this.snackBar.success(this.translate.instant(T + 'MSG.UPDATED'));
          this.load();
        } else {
          // New templates open straight in the editor.
          this.snackBar.success(this.translate.instant(T + 'MSG.CREATED'));
          this.open(detail.template);
        }
      });
  }

  setDefault(template: WorkClassificationTemplate): void {
    this.service.setDefault(template.id).subscribe({
      next: () => {
        this.snackBar.success(this.translate.instant(T + 'MSG.DEFAULT_SET'));
        this.load();
      },
      error: err => this.showError(err, { TEMPLATE_INACTIVE: T + 'ERROR.DEFAULT_INACTIVE' }),
    });
  }

  deactivate(template: WorkClassificationTemplate): void {
    let message = this.translate.instant(T + 'CONFIRM_DEACTIVATE', { name: template.name });
    if (template.isDefault) {
      message += ' ' + this.translate.instant(T + 'CONFIRM_DEACTIVATE_DEFAULT');
    }
    this.confirm.confirm(
      message,
      this.translate.instant(T + 'MENU_DEACTIVATE'),
      () => {
        this.service.deactivate(template.id).subscribe({
          next: () => {
            this.snackBar.success(this.translate.instant(T + 'MSG.DEACTIVATED'));
            this.load();
          },
          error: err => this.showError(err),
        });
      },
      () => {},
      this.translate.instant(T + 'MENU_DEACTIVATE'),
      'warn',
      'delete',
    );
  }

  reactivate(template: WorkClassificationTemplate): void {
    this.service.setActive(template.id, true).subscribe({
      next: () => {
        this.snackBar.success(this.translate.instant(T + 'MSG.REACTIVATED'));
        this.load();
      },
      error: err => this.showError(err),
    });
  }

  private showError(err: unknown, overrides: Record<string, string> = {}): void {
    const m = mapTemplateError(err, overrides);
    this.snackBar.error(this.translate.instant(m.key, m.params));
  }
}
